import React from 'react';
import { CloseButton, Flex } from '@mantine/core';
import { Link } from 'react-router-dom';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import {
	formatPerUnitMoney,
	formatSummaryDecimal,
} from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';
import { CurrencyType } from '@dealstack/shared/lib/generated';
import { CURRENCY_SYMBOLS } from '@dealstack/shared/lib/format/currency/currency';
import { useTranslationValuations } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { VALUATIONS_ROUTES } from '@dealstack/frontend/app/pages/Valuations/shared/valuationRoutes';
import { useValuationNavigation } from '@dealstack/frontend/app/pages/Valuations/hooks/useValuationNavigation';
import {
	PopoverContainer,
	PopoverTitle,
	ItemsGrid,
	SecurityName,
	UnitsText,
	PriceLink,
	TotalAmount,
	TotalRowSeparator,
	TotalRowLabel,
	TotalRowAmount,
	ParenFiller,
} from './styled';
import { BreakdownItem } from '@dealstack/frontend/app/pages/Valuations/components/BreakdownPopover/BreakdownPopover.interface';
import { getGridColumns } from '@dealstack/frontend/app/pages/Valuations/components/BreakdownPopover/BreakdownPopover.utils';

interface Props {
	items: BreakdownItem[];
	showSecurityName?: boolean;
	showTotal?: boolean;
	currency?: CurrencyType;
	// When set, the footer total row shows this pre-formatted string instead of
	// the default sum of `item.amount`. Use this when the footer represents a
	// derived value (e.g. a percentage) rather than a sum of the rows.
	totalText?: string;
	onClose: () => void;
}

export const BreakdownPopover = ({
	items,
	showSecurityName = false,
	showTotal = false,
	currency,
	totalText,
	onClose,
}: Props) => {
	const tValuations = useTranslationValuations();
	const { dealId, pathValuationId } = useValuationNavigation();

	if (items.length === 0) {
		return null;
	}

	const currencySymbol = currency ? CURRENCY_SYMBOLS[currency] : '';

	const showTotalRow = showTotal && items.length > 1;
	const totalAmount = items.reduce(
		(acc, item) => acc.plus(item.amount),
		new HighPrecisionDecimal(0)
	);
	const totalLabel = tValuations('sources_and_uses.total');
	const totalDisplay = totalText ?? formatSummaryDecimal(totalAmount);

	// The formatter wraps negative numbers in parentheses. Right-aligning a mix
	// of positives and negatives makes the digits drift left for positives — so
	// we render an invisible `)` after non-parenthesised values to reserve its
	// width and keep digit columns aligned.
	const withParenAlignment = (text: string) => {
		if (text.endsWith(')')) {
			return text;
		}
		return (
			<>
				{text}
				<ParenFiller>)</ParenFiller>
			</>
		);
	};

	return (
		<PopoverContainer $wide={showSecurityName && showTotal}>
			<Flex justify="space-between" align="center" style={{ width: '100%' }}>
				<PopoverTitle>{tValuations('summary.breakdown_popover')}</PopoverTitle>
				<CloseButton size="sm" onClick={onClose} />
			</Flex>
			<ItemsGrid
				$columns={getGridColumns(showSecurityName, showTotal)}
				$centerContent={!showTotal}
			>
				{items.map((item, index) => {
					const units = item.units;
					const hasUnits = units && !units.isZero();
					const valuePerUnit = hasUnits
						? item.amount.div(units)
						: new HighPrecisionDecimal(0);
					const priceText = formatPerUnitMoney(valuePerUnit, currencySymbol);

					return (
						<React.Fragment key={index}>
							{showSecurityName && <SecurityName>{item.name}</SecurityName>}
							<UnitsText>
								{hasUnits ? (
									<>
										{formatSummaryDecimal(item.units)} &times;{' '}
										{dealId && pathValuationId && item.issuerId ? (
											<Link
												to={VALUATIONS_ROUTES.breakpointSummary({
													dealId,
													valuationId: pathValuationId,
													personId: item.issuerId,
													openDrawer: 'true',
												})}
											>
												<PriceLink>{priceText}</PriceLink>
											</Link>
										) : (
											<PriceLink>{priceText}</PriceLink>
										)}
									</>
								) : null}
							</UnitsText>
							{showTotal && (
								<TotalAmount>
									{withParenAlignment(formatSummaryDecimal(item.amount))}
								</TotalAmount>
							)}
						</React.Fragment>
					);
				})}
				{showTotalRow && (
					<>
						<TotalRowSeparator />
						<TotalRowLabel>{totalLabel}</TotalRowLabel>
						{showSecurityName && <span />}
						<TotalRowAmount>{withParenAlignment(totalDisplay)}</TotalRowAmount>
					</>
				)}
			</ItemsGrid>
		</PopoverContainer>
	);
};
